import { useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import * as Sentry from '@sentry/react';
import { useParams, useOutletContext } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { selectCurrentLanguage, selectMainPlace, selectLayout } from '../../redux/selectors';
import type { Place } from '../../types/place';
import { APP_IMJ_DEMO } from '../../constants/apps';
import { getProgress, setProgress } from '../../services/localStorage';

import { getPlace } from '../../services/data';
import StationPage from '../../pages/station/StationPage';
import DataDisplay from '../../pages/DataDisplay';
import '../../pages/GalleriesPage.module.css'
import StationPageV2 from '../../pages/station/StationPageV2';
import TodGalleryList from '../../pages/tod/TodGalleryList';
import TodExhibitList from '../../pages/tod/TodExhibitList';
import TodVideoPage from '../../pages/tod/TodVideoPage';
import { setChildren, setCurrentPlace, setLayout } from '../../redux/actions/treeLevelActions';
import { trackSpaceViewed, trackStationViewed } from '../../mixpanel'
import { handleError, isNetworkError } from '../../utils/errorHandler';
import { useViewedDataProgress } from '../../hooks/useViewedDataProgress';
import { useTranslation } from 'react-i18next';

interface OutletContextType {
  isWithProgress: boolean;
}

export default function DynamicPlacePage() {
  const { placeId, slug } = useParams();
  const dispatch = useAppDispatch()
  const currentLanguage = useAppSelector(selectCurrentLanguage)
  const mainPlace = useAppSelector(selectMainPlace)
  const layout = useAppSelector(selectLayout)
  const {isWithProgress} = useOutletContext<OutletContextType>()
  const { t } = useTranslation()
  const [place, setPlace] = useState<Place | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  const [isNetworkErr, setIsNetworkErr] = useState<boolean>(false)

  const {createParent, markChildViewed} = useViewedDataProgress(slug)
  const trackedPlaceId = useRef<string | null>(null)

  // The footer's prev/next arrows walk `treeLevel.childrenIds`, but that list is
  // only (re)filled when a place WITH children loads — a leaf dispatches
  // setChildren([]) and the reducer keeps whatever was there. Fine when the
  // visitor came from the parent space; wrong when a station is reached via the
  // keypad, a QR deep-link, the map, or a persisted store from an earlier visit
  // (MUZA "מרכז אדם ועמלו": both arrows dark). Fill the list from the station's
  // own parent instead. Best-effort and non-blocking: the station renders first.
  const latestPlaceId = useRef<string | undefined>(placeId)
  latestPlaceId.current = placeId

  const syncSiblingsFromParent = async (parentId: number | string, forPlaceId: string, language: string) => {
    try {
      const parent = await getPlace(parentId, language)
      if (latestPlaceId.current !== forPlaceId) return // visitor already moved on
      if (parent?.children_list?.length) {
        dispatch(setChildren(parent.children_list))
      }
    } catch (err: unknown) {
      if (!isNetworkError(err)) Sentry.captureException(err)
    }
  }

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        setError(null);
        setIsNetworkErr(false);
        const place = await getPlace(placeId!, currentLanguage ?? 'en');
        if (!place) return;

        dispatch(setChildren(place.children_list ?? []))
        dispatch(setLayout(place.children_layout ?? ''))
        dispatch(setCurrentPlace({title: place.information_title}))

        if (!place.children_list?.length && place.parent_id !== null && place.parent_id !== undefined) {
          void syncSiblingsFromParent(place.parent_id, placeId!, currentLanguage ?? 'en')
        }

        // Only track analytics once per place navigation, not on language changes
        const shouldTrack = trackedPlaceId.current !== placeId;
        if (shouldTrack) trackedPlaceId.current = placeId!;

        setPlace(() => {
          if (place.children_list && place.children_list.length > 0) {
            if (shouldTrack) {
              trackSpaceViewed(place.information_title, place.type)
            }
              if (isWithProgress) {
                const filteredChildren = (mainPlace?.children ?? []).filter((child: Place) =>
                  child.type !== 'intro' && child.thumbnail_type !== 'minor'
                );
                const amountOfChildren = filteredChildren.length;

                const parsedProgress = getProgress(slug ?? '');
                if (parsedProgress) {
                    if (parsedProgress[placeId!] && parsedProgress[placeId!].amountOfChildren !== amountOfChildren) {
                        parsedProgress[placeId!].amountOfChildren = amountOfChildren;
                        setProgress(slug ?? '', parsedProgress);
                    }
                }

                createParent(placeId!, amountOfChildren)
              }
          } else {
            if (shouldTrack) {
              trackStationViewed(place.information_title, place.type)
            }
            if (isWithProgress) {
              markChildViewed(place.parent_id ?? 0, placeId!)
            }
          }
          return place
        })
      } catch (err: unknown) {
        const networkErr = isNetworkError(err);
        setIsNetworkErr(networkErr);
        if (!networkErr) {
          Sentry.captureException(err);
        }
        setError(networkErr
          ? t('common.error-network')
          : ((err as Error)?.message || 'Failed to load content'));
        handleError(err)
      } finally {
        setIsLoading(false)
      }
    }

    if (placeId) {
      fetchData()
    }
  }, [placeId, currentLanguage])
  

  if (error && !isLoading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', padding: '2rem' }}>
        <p style={{ fontSize: '2rem', marginBottom: '1rem' }}>{isNetworkErr ? '📡' : '⚠'}</p>
        <p style={{ textAlign: 'center', marginBottom: '1rem' }}>{error}</p>
        <button
          onClick={() => { setError(null); setIsLoading(true); window.location.reload(); }}
          style={{ padding: '8px 24px', border: '1px solid #ccc', borderRadius: '4px', background: 'transparent', cursor: 'pointer' }}
        >
          {t('common.try-again')}
        </button>
      </Box>
    );
  }

  const placeMatchesRoute = place && String(place.id) === String(placeId);

  if (placeMatchesRoute) {
    const hasChildren = place.children_list && place.children_list.length > 0;
    const isStation = place.type.toLowerCase() === 'station' || place.type.toLowerCase() === 'story';

    if (layout === 'tod' && !isLoading) {
      if (hasChildren && place.type.toLowerCase() !== 'story') {
        const isRootSpace = place.parent_id === null || place.parent_id === undefined || place.id === mainPlace?.id;
        if (isRootSpace) {
          return <TodGalleryList translations={place.translations} childrenIds={place.children_list} />;
        }
        return <TodExhibitList place={place} childrenIds={place.children_list} />;
      }
      if (isStation) {
        return <TodVideoPage station={place} />;
      }
    }

    if (!isLoading && hasChildren && place.type.toLowerCase() !== 'story') {
      return <DataDisplay key={placeId} {...place} parentId={placeId!} parentTitle={place.information_title} />
    } else if (!isLoading && isStation) {
      if (slug === APP_IMJ_DEMO) {
        return <StationPage placeId={placeId!} />
      } else {
        return <StationPageV2 id={placeId!} initialPlace={place}/>
      }
    }
  }

  return null;
}
